import type {
  PreviewModule,
  TaskAssignable,
  TaskDetail,
  TaskKind,
  TaskList,
  TaskRuleView,
  TaskSummary,
  TaskTypeView,
  TaskView,
} from "@dentacore/shared-types";

import { api, atLocation } from "./client";

/**
 * Tasks (STORY-198, TK-D1–TK-D38). Every `/tasks` call is one location's: the
 * list is read per location (`across`, UR-D11), and a call naming a task goes
 * to the location it was read from (`locate`), whatever the active one is.
 */

export function readPreviews(signal?: AbortSignal): Promise<PreviewModule[]> {
  return api<PreviewModule[]>("/clinic/modules/previews", { signal });
}

export function listTasks(
  list: TaskList,
  view: TaskView,
  signal?: AbortSignal,
): Promise<TaskSummary[]> {
  return api<TaskSummary[]>(`/tasks?list=${list}&view=${view}`, { signal });
}

export function getTask(taskId: string, signal?: AbortSignal): Promise<TaskDetail> {
  return api<TaskDetail>(`/tasks/${taskId}`, { signal });
}

export interface NewTask {
  typeId: string;
  title: string;
  description?: string;
  roleId?: string;
  assigneeId?: string;
  dueAt?: string | null;
  patientId?: string;
  inventoryItemId?: string;
}

export function createTask(body: NewTask): Promise<TaskDetail> {
  return api<TaskDetail>("/tasks", { method: "POST", body });
}

type Action = "take" | "give-back" | "wake" | "resolve" | "reopen";

export function actOnTask(taskId: string, action: Action): Promise<TaskDetail> {
  return api<TaskDetail>(`/tasks/${taskId}/${action}`, { method: "POST", body: {} });
}

export function noteOnTask(taskId: string, body: string): Promise<TaskDetail> {
  return api<TaskDetail>(`/tasks/${taskId}/notes`, { method: "POST", body: { body } });
}

export function assignTask(
  taskId: string,
  to: { roleId: string } | { assigneeId: string },
): Promise<TaskDetail> {
  return api<TaskDetail>(`/tasks/${taskId}/assign`, { method: "POST", body: to });
}

export function snoozeTask(taskId: string, until: string): Promise<TaskDetail> {
  return api<TaskDetail>(`/tasks/${taskId}/snooze`, { method: "POST", body: { until } });
}

export function closeTask(taskId: string, reason: string): Promise<TaskDetail> {
  return api<TaskDetail>(`/tasks/${taskId}/close`, { method: "POST", body: { reason } });
}

export function listTaskTypes(signal?: AbortSignal): Promise<TaskTypeView[]> {
  return api<TaskTypeView[]>("/task-types", { signal });
}

export function addTaskType(name: string): Promise<TaskTypeView> {
  return api<TaskTypeView>("/task-types", { method: "POST", body: { name } });
}

export function changeTaskType(
  typeId: string,
  body: { name?: string; archived?: boolean },
): Promise<TaskTypeView> {
  return api<TaskTypeView>(`/task-types/${typeId}`, { method: "PATCH", body });
}

export function listTaskRules(signal?: AbortSignal): Promise<TaskRuleView[]> {
  return api<TaskRuleView[]>("/task-rules", { signal });
}

export function setTaskRule(
  kind: TaskKind,
  body: {
    enabled: boolean;
    roleId?: string | null;
    toVisitDentist?: boolean;
    waitAmount?: number | null;
    dueDays?: number | null;
  },
): Promise<TaskRuleView[]> {
  return api<TaskRuleView[]>(`/task-rules/${kind}`, { method: "PUT", body });
}

/** Who a task at this location can go to — asked of the task's own location. */
export function readAssignable(branchId: string, signal?: AbortSignal): Promise<TaskAssignable> {
  return atLocation(branchId, () => api<TaskAssignable>("/tasks/assignable", { signal }));
}
